import { FlexColumn } from '@fox/layout/src/flex';
import { FormLabel } from '@mui/material';
import React, { useEffect } from 'react';
import styled from 'styled-components';
import { roundNumber } from '../../utils/number-functions';
import { ExtendedTextFieldProps, TextField } from '../text-field/index';

const NumberInputLabel = styled(FormLabel)`
	margin-bottom: 0.25rem;
`;

export type NumberUnitLabel = 'px' | '%' | (string & {});

type NumberInputSettings = {
	/**
	 * If true, forces values outside of the specified range to min or max value, whichever is closest.
	 * This behavior happens on blur.
	 */
	clamp?: boolean;

	/**
	 * If true, allows clearing values when clamp is true.
	 */
	allowClearInput?: boolean;

	/**
	 * Minimum value. This is an html attribute for the input element.
	 */
	min?: number;

	/**
	 * Maximum value. This is an html attribute for the input element.
	 */
	max?: number;

	/**
	 * The increment that the arrow buttons increase and decrease the value by. Also applies to a focused input
	 * when using arrow keys or scroll wheel.
	 */
	step?: number;

	/**
	 * A callback to be fired on input change event. The val arg will always be a number if `zeroDefault` is true,
	 * otherwise it will be either a number or empty string.
	 */
	onChange?: (val: number | '') => void;

	/**
	 * The value of the input.
	 */
	value?: number | '';

	/**
	 * If true, the input will default to 0 zero when no value is entered
	 * (by default, this controlled component defaults to an empty string).
	 */
	zeroDefault?: boolean;

	/**
	 * An optional function to be fired during the onBlur event after internal handleBlur processing.
	 */
	onBlur?: (val: number | '') => void;

	/**
	 * Rounds input to specified decimal places
	 */
	fixedDecimals?: number;
};

export type NumberInputProps = NumberInputSettings &
	Omit<ExtendedTextFieldProps, 'value' | 'onChange'> & {
		/**
		 * The unit label appended at the end of the input. While any string will be accepted, for consistency's sake,
		 * an existing label from the `NumberUnitLabel` type should be used. If a suitable label isn't available, it should
		 * be added to the type.
		 */
		unitLabel?: NumberUnitLabel;
	};

function useNumberInput({ clamp, allowClearInput, min, max, step, value, zeroDefault, onChange, onBlur, fixedDecimals }: NumberInputSettings) {
	const defaultValue = zeroDefault ? 0 : '';
	const [fieldValue, setFieldValue] = React.useState<string | number | undefined>(value ?? defaultValue);
	const [rangeError, setRangeError] = React.useState<string | null>(null);
	const hasMin = !!min || min === 0;
	const hasMax = !!max || max === 0;

	useEffect(() => {
		setFieldValue(value);
	}, [value]);

	const getRangeErrorMessage = (min, max) => {
		if (!hasMin) return `Range: < ${max}`;
		if (!hasMax) return `Range: > ${min}`;
		return `Range: ${min} - ${max}`;
	};

	const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
		const value = Number(event.target.value);
		if (!clamp || (!hasMin && !hasMax)) return;

		if (clamp && allowClearInput && event.target.value === '') {
			setFieldValue('');
			onChange?.('');
			onBlur?.('');
			setRangeError(null);
			return;
		}
		if (!hasMin && hasMax && value > max) {
			setFieldValue(max);
			onChange?.(max);
			onBlur?.(max);
			setRangeError(null);
			return;
		}
		if (!hasMax && hasMin && value < min) {
			setFieldValue(min);
			onChange?.(min);
			onBlur?.(min);
			setRangeError(null);
			return;
		}
		if ((hasMin && value < min) || (hasMax && value > max)) {
			const clampedValue = Math.max(min, Math.min(max, value));
			setFieldValue(clampedValue);
			onChange?.(clampedValue);
			onBlur?.(clampedValue);
			setRangeError(null);
			return;
		}
		onBlur?.(value);
	};

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value === '' ? defaultValue : Number(event.target.value);
		const isNumber = typeof value === 'number';
		const fixedValue = fixedDecimals !== undefined && isNumber ? roundNumber(value, fixedDecimals) : value;
		const fixedValueIsNumber = typeof fixedValue === 'number';

		if (!fixedValueIsNumber) {
			setFieldValue(defaultValue);
			onChange?.(defaultValue);
			return;
		} else if ((min && fixedValue < min) || (max && fixedValue > max)) {
			setFieldValue(fixedValue);
			setRangeError(getRangeErrorMessage(min, max));
			return;
		}

		setFieldValue(fixedValue);
		setRangeError(null);
		onChange?.(fixedValue);
	};

	const currentValue = fieldValue || fieldValue === 0 ? Number(fieldValue).toString() : defaultValue;
	return {
		onChange: handleChange,
		onBlur: handleBlur,
		value: currentValue,
		InputProps: {
			onBlur: handleBlur,
			inputProps: {
				type: 'number',
				step: step,
				min: min,
				max: max
			}
		},
		rangeError
	};
}

/**
 * An extension of the TextField component for numeric values with some common features built in, including clamping of min/max values, unit suffix, and defaulting to 0.
 */
export const ConsistentNumberInput = ({ label, helperText, ...rest }: NumberInputProps) => {
	return (
		<FlexColumn>
			{label ? <NumberInputLabel>{label}</NumberInputLabel> : null}
			<NumberInput
				// Fallback to empty space to retain horizontal alignment when helperText renders beneath
				helperText={helperText ?? ' '}
				{...rest}
			/>
		</FlexColumn>
	);
};

export const NumberInput = ({
	clamp,
	allowClearInput,
	min,
	max,
	step,
	unitLabel,
	value,
	zeroDefault,
	helperText,
	onChange,
	onBlur,
	fixedDecimals,
	variant,
	...rest
}: NumberInputProps) => {
	const { rangeError, ...h } = useNumberInput({ clamp, allowClearInput, min, max, step, value, zeroDefault, onChange, onBlur, fixedDecimals });
	return (
		<TextField
			size="small"
			error={!!rangeError || rest.error}
			helperText={rangeError ?? helperText}
			endAdornment={unitLabel}
			variant={variant}
			{...h}
			{...rest}
		/>
	);
};
